/**
 * Every Analytics panel type there is. One file per renderer, holding the names that renderer
 * draws and the settings they read, so adding a type is one new file plus one line in the list
 * below. The order of that list is the order every generated list uses: the display-type picker,
 * the tool schema's enum, and the display type list and guide index in the system prompt.
 *
 * The React parts of a type (its icon, its panel body, its editor) cannot live here because these
 * files are also loaded by the server. They live in the frontend's own registry, keyed by the
 * same ids.
 */

/*
 * What a file declares:
 *
 * - `names`        the display types this renderer draws. Several names share one file when they
 *                  need the same settings: bar and line, or the three value-against-target names.
 * - `block`        the key under `config.display` holding this renderer's settings. A table's
 *                  settings are its columns, which any type may also carry, so `columns` is a block
 *                  here without being table-only.
 * - `sharedBlock`  true when the block is not one type's settings, so the tool declares it by hand
 *                  beside the properties every panel carries and the surfaces that walk one block
 *                  per type leave it out. `columns` and `format` are. This says nothing about who
 *                  may be written one — that is `carriedBy`, below. A shared block still declares
 *                  one when only some renderers draw it (`columns`) and declares none when every
 *                  type draws it (`format`: there is no display type that shows no values).
 * - `carriedBy`    the type ids whose renderer DRAWS this block. A write to a type outside the list
 *                  is refused, because storing settings nothing draws is a silent no-op — the
 *                  failure this gate exists to prevent. A file declaring one may name no types of
 *                  its own (`names: []`): it is then a block rather than a renderer, and a type
 *                  joins it by one id here plus its renderer drawing it.
 * - `fields`       what the block accepts: each field's kind, whether it is required, whether an
 *                  empty string clears it, an optional `hint` explaining why a field is shaped the
 *                  way it is, which validation appends to the rejection message, and what the
 *                  visual editor needs to draw a control per field (`label` above the control,
 *                  `ariaLabel` where the label alone is too terse, `valueLabels` naming an enum's
 *                  choices). Four things read this: the merge behind the panel-update tool, the
 *                  advanced-mode check on a returned config, the tool definition declaring the same
 *                  fields to the model, and the editor. So a new config option is one line here,
 *                  and it inherits "an update never overwrites what it did not mention" for free.
 * - `validate`     the rule this block needs beyond what its fields can say: a rule about two
 *                  fields at once, one that depends on which name is using a shared block, a string
 *                  format no field kind has a word for. Takes a block display code returned and the
 *                  panel's display type, and returns the rejection message, or an empty string when
 *                  the block is fine. Runs after the block's own fields have passed, so it can
 *                  trust their types.
 * - `editorHint`   the sentence under the block's controls, telling the reader what one row becomes
 *                  and what the query owes the panel. Per block rather than per field, and only for
 *                  the blocks whose editor is drawn from the fields; a hand-written editor keeps
 *                  its own.
 * - `stateSummary` how the block reads on the `- Display:` line of the panel state the model is
 *                  given every turn. Takes the stored block and the panel's display type, and
 *                  returns the text inside the parentheses, or an empty string when there is
 *                  nothing worth saying yet. A block without one tells the model its type and
 *                  nothing else.
 * - `toolSchema`   the block as the panel update tool declares it to the model: what it is for, its
 *                  required keys, and a description per field. Absent on the shared block, which the
 *                  tool declares beside the properties every panel carries.
 *
 * What a name declares:
 *
 * - `id`             the value stored in `config.display.type`.
 * - `label`          the type's name in the UI, sentence case.
 * - `phrase`         the same name inside a sentence ("shown as a bar chart"). Absent when no
 *                    surface says it that way yet.
 * - `guide`          the file under `immutable/analytics/panel-types/` teaching the agent when to
 *                    choose this type. Filenames do not always match the id.
 * - `container`      true when the type holds other panels instead of data.
 * - `advanced`       true when display code may return this type. HTML is excluded because its
 *                    markup is compiled and validated when the panel cooks; containers are
 *                    excluded because they hold panels rather than a display.
 * - `creationLayout` a size written onto the stored layout once, when the type is picked. The
 *                    panel stays freely resizable afterwards.
 * - `toolSummary`    what the type is for, in one phrase, printed after its id in the list of
 *                    display types the panel update tool offers the model. Absent when the id says
 *                    enough on its own.
 * - `promptSummary`  the same thing for the analytics mode system prompt, where the model first
 *                    reads what it can build with. Longer than `toolSummary`, and required: a type
 *                    the prompt cannot describe is a type the model never picks.
 * - `reportFormat`   true when this type is one of the shapes a report page's panel takes (Written,
 *                    Document). A report page holds only these; the ones that are not plain
 *                    `report` exist only on report pages.
 * - `formatLabel`    the name of the format in a report's Format control ("Written").
 */
import columns from './columns.js';
import format from './format.js';
import links from './links.js';
import style from './style.js';
import annotations from './annotations.js';
import tooltip from './tooltip.js';
import chart from './chart.js';
import bump from './bump.js';
import scatter from './scatter.js';
import pie from './pie.js';
import treemap from './treemap.js';
import cellGrid from './cellGrid.js';
import activity from './activity.js';

import statusGrid from './statusGrid.js';
import funnel from './funnel.js';
import waterfall from './waterfall.js';
import sankey from './sankey.js';
import chord from './chord.js';
import statetimeline from './statetimeline.js';
import stat from './stat.js';
import trend from './trend.js';
import target from './target.js';
import report from './report.js';
import document from './document.js';
import html from './html.js';
import map from './map.js';
import region from './region.js';
import tabs from './tabs.js';
import group from './group.js';

const analyticsPanelRenderers = [columns, format, links, style, annotations, tooltip, chart, bump, scatter, pie, treemap, cellGrid, statusGrid, activity, funnel, waterfall, sankey, chord, statetimeline, stat, trend, target, report, document, html, map, region, tabs, group];

export default analyticsPanelRenderers;
